import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasDashboardFullAccess } from '../../utils/workspaces';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import HodThread, {
  HOD_STATUS_META,
  HodAvatar,
  HodStatusBadge,
  hodFullDate,
  hodTimeAgo,
} from '../../components/HodThread';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'pending', label: 'Pending' },
  { key: 'in process', label: 'In process' },
  { key: 'solved', label: 'Solved' },
];

const toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2200, timerProgressBar: true });

function StatCard({ label, value, tone, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border bg-white px-4 py-3 text-left shadow-sm transition hover:shadow-md ${active ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200'}`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tone}`}>{value}</p>
    </button>
  );
}

export default function HodNotifications() {
  const isSuperAdmin = hasDashboardFullAccess(AuthService.getUser());

  const [notifications, setNotifications] = useState([]);
  const [currentStaffId, setCurrentStaffId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const detailRef = useRef(null);
  const firstLoadDone = useRef(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/hod-notifications');
      const list = res.data?.notifications || [];
      setNotifications(list);
      setCurrentStaffId(res.data?.currentStaffId || null);
      if (!firstLoadDone.current) {
        firstLoadDone.current = true;
        if (list.length) setSelectedId(list[0].notificationId);
      }
    } catch (err) {
      if (!silent) Swal.fire('Error', err?.response?.data?.message || 'Failed to load messages.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => load(true), 30000);
    return () => clearInterval(timer);
  }, [load]);

  const replaceNotification = (updated) => {
    setNotifications((prev) => prev
      .map((n) => (n.notificationId === updated.notificationId ? updated : n))
      .sort((a, b) => new Date(b.lastActivityAt) - new Date(a.lastActivityAt)));
  };

  const counts = useMemo(() => {
    const c = { all: notifications.length, unread: 0, pending: 0, 'in process': 0, solved: 0 };
    notifications.forEach((n) => {
      c[n.status] = (c[n.status] || 0) + 1;
      if (n.unread) c.unread += 1;
    });
    return c;
  }, [notifications]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notifications.filter((n) => {
      if (filter === 'unread' && !n.unread) return false;
      if (filter !== 'all' && filter !== 'unread' && n.status !== filter) return false;
      if (!q) return true;
      return n.subject.toLowerCase().includes(q)
        || n.message.toLowerCase().includes(q)
        || (n.creator?.name || '').toLowerCase().includes(q);
    });
  }, [notifications, filter, search]);

  const selected = notifications.find((n) => n.notificationId === selectedId) || null;

  const select = (n) => {
    setSelectedId(n.notificationId);
    if (n.unread) {
      setNotifications((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, unread: false } : x)));
      axiosInstance.post(`/admin/hod-notifications/${n.notificationId}/seen`).catch(() => {});
    }
    if (window.innerWidth < 1024 && detailRef.current) {
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };

  // The first message opened automatically on load should also count as read.
  useEffect(() => {
    if (selected?.unread) {
      setNotifications((prev) => prev.map((x) => (x.notificationId === selected.notificationId ? { ...x, unread: false } : x)));
      axiosInstance.post(`/admin/hod-notifications/${selected.notificationId}/seen`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const changeStatus = async (n, status) => {
    if (status === 'solved') {
      const result = await Swal.fire({
        icon: 'question',
        title: 'Mark this as solved?',
        text: 'The lecturer will see it as solved. You can reopen it later if needed.',
        showCancelButton: true,
        confirmButtonText: 'Yes, mark solved',
        confirmButtonColor: '#059669',
      });
      if (!result.isConfirmed) return;
    }
    setChangingStatus(true);
    try {
      const res = await axiosInstance.put(`/admin/hod-notifications/${n.notificationId}/status`, { status });
      replaceNotification(res.data.notification);
      toast.fire({ icon: 'success', title: `Marked ${HOD_STATUS_META[status].label.toLowerCase()}` });
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to update the status.', 'error');
    } finally {
      setChangingStatus(false);
    }
  };

  const postComment = async (id, comment) => {
    try {
      const res = await axiosInstance.post(`/admin/hod-notifications/${id}/comments`, { comment });
      replaceNotification(res.data.notification);
      return true;
    } catch (err) {
      Swal.fire('Could not post', err?.response?.data?.message || 'Please try again.', 'error');
      return false;
    }
  };

  const deleteComment = async (id, commentId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this comment?',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.delete(`/admin/hod-notifications/${id}/comments/${commentId}`);
      replaceNotification(res.data.notification);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete the comment.', 'error');
    }
  };

  const deleteMessage = async (n) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this message?',
      text: 'The whole conversation will be removed permanently.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/hod-notifications/${n.notificationId}`);
      setNotifications((prev) => prev.filter((x) => x.notificationId !== n.notificationId));
      setSelectedId(null);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete the message.', 'error');
    }
  };

  return (
    <DashboardLayout title="Lecturer Messages">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Lecturer Messages</h1>
        <p className="mt-1 text-sm text-gray-600">
          Messages lecturers have sent to the HOD. Only super admins and management can see this page.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatCard label="All" value={counts.all} tone="text-gray-900" active={filter === 'all'} onClick={() => setFilter('all')} />
        <StatCard label="Unread" value={counts.unread} tone="text-rose-600" active={filter === 'unread'} onClick={() => setFilter('unread')} />
        <StatCard label="Pending" value={counts.pending} tone="text-amber-600" active={filter === 'pending'} onClick={() => setFilter('pending')} />
        <StatCard label="In process" value={counts['in process']} tone="text-blue-600" active={filter === 'in process'} onClick={() => setFilter('in process')} />
        <StatCard label="Solved" value={counts.solved} tone="text-emerald-600" active={filter === 'solved'} onClick={() => setFilter('solved')} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Inbox list */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-3">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search subject, message or lecturer…"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
              />
              <div className="mt-2 flex flex-wrap gap-1 text-xs font-semibold">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`rounded-full px-2.5 py-1 transition ${filter === f.key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {f.label} ({counts[f.key] || 0})
                  </button>
                ))}
              </div>
            </div>

            <div className="max-h-[70vh] overflow-y-auto">
              {loading ? (
                <p className="py-10 text-center text-sm text-gray-400">Loading…</p>
              ) : visible.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-400">
                  {notifications.length === 0 ? 'No messages from lecturers yet.' : 'No messages match.'}
                </p>
              ) : (
                visible.map((n) => {
                  const meta = HOD_STATUS_META[n.status] || HOD_STATUS_META.pending;
                  const last = n.comments.length ? n.comments[n.comments.length - 1].comment : n.message;
                  const active = n.notificationId === selectedId;
                  return (
                    <button
                      key={n.notificationId}
                      type="button"
                      onClick={() => select(n)}
                      className={`flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left transition last:border-b-0 ${active ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                    >
                      <HodAvatar person={n.creator} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`truncate text-sm ${n.unread ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>
                            {n.creator?.name || 'Unknown lecturer'}
                          </span>
                          <span className="shrink-0 text-[11px] text-gray-400" title={hodFullDate(n.lastActivityAt)}>
                            {hodTimeAgo(n.lastActivityAt)}
                          </span>
                        </div>
                        <p className={`truncate text-sm ${n.unread ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{n.subject}</p>
                        <p className="truncate text-xs text-gray-500">{last}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600`}>
                            <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                            {meta.label}
                          </span>
                          {n.comments.length > 0 && <span className="text-[11px] text-gray-400">· {n.comments.length} {n.comments.length === 1 ? 'reply' : 'replies'}</span>}
                          {n.unread && <span className="ml-auto h-2.5 w-2.5 rounded-full bg-rose-500" title="Unread" />}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div className="lg:col-span-3" ref={detailRef}>
          {!selected ? (
            <div className="flex h-64 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-sm text-gray-400">
              Select a message to read and reply.
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <HodAvatar person={selected.creator} size="h-11 w-11" textSize="text-base" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-gray-900">{selected.creator?.name || 'Unknown lecturer'}</p>
                    <p className="text-xs text-gray-500">
                      {selected.creator?.employeeNumber ? `#${selected.creator.employeeNumber} · ` : ''}
                      Sent {hodFullDate(selected.createdAt)}
                    </p>
                  </div>
                </div>
                <HodStatusBadge status={selected.status} />
              </div>

              <h2 className="mt-4 text-lg font-bold text-gray-900">{selected.subject}</h2>
              <p className="mt-2 whitespace-pre-wrap break-words rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-800">{selected.message}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {selected.status === 'pending' && (
                  <button
                    type="button"
                    disabled={changingStatus}
                    onClick={() => changeStatus(selected, 'in process')}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                  >
                    Start working
                  </button>
                )}
                {selected.status !== 'solved' && (
                  <button
                    type="button"
                    disabled={changingStatus}
                    onClick={() => changeStatus(selected, 'solved')}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Mark solved
                  </button>
                )}
                {selected.status === 'in process' && (
                  <button
                    type="button"
                    disabled={changingStatus}
                    onClick={() => changeStatus(selected, 'pending')}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                  >
                    Move back to pending
                  </button>
                )}
                {selected.status === 'solved' && (
                  <button
                    type="button"
                    disabled={changingStatus}
                    onClick={() => changeStatus(selected, 'in process')}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                  >
                    Reopen
                  </button>
                )}
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => deleteMessage(selected)}
                    className="ml-auto rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100"
                  >
                    Delete
                  </button>
                )}
              </div>
              {selected.status === 'pending' && (
                <p className="mt-2 text-xs text-gray-400">Posting an update below also moves this to “In process” automatically.</p>
              )}

              <div className="mt-5 border-t border-gray-100 pt-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Conversation</p>
                <HodThread
                  notification={selected}
                  currentStaffId={currentStaffId}
                  isSuperAdmin={isSuperAdmin}
                  viewerIsHod
                  onComment={postComment}
                  onDeleteComment={deleteComment}
                  composerPlaceholder="Post an update or reply — the lecturer will see it…  (Enter to post)"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
